import { FC, useContext, useEffect, useRef } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import NotificationDispatch, { showErrorNotification } from "../../../context/notificationContext";
import { useI18n } from "../../../hooks/useTranslations";
import { useDefaultTaskCategoriesApi } from "../../../http/defaultTaskCategories/api";
import { DefaultTaskCategories } from "../../../http/defaultTaskCategories/types";
import { DefaultTask } from "../../../http/defaultTasks/types";
import { useDueDateTypesApi } from "../../../http/dueDateTypes/api";
import { DueDateType } from "../../../http/dueDateTypes/types";
import { PaginationQueryWithFields } from "../../../types";
import Form from "../../ui/Form";
import FormField from "../../ui/FormField";
import FormFieldsContainer from "../../ui/FormFieldsContainer";
import Input from "../../ui/Input";
import MultiCheckbox from "../../ui/MultiCheckbox";
import reactSelectStyles from "../../ui/ReactSelectUtils";

const DUE_DATE_TYPE_FIELDS = ["id", "name", "versionNumber"] as const;

export interface DefaultTaskFormBody {
  name: string;
  defaultDueDateType?: DueDateType;
  categories: string[];
}

type DefaultTaskFormProps = {
  task?: DefaultTask | null;
  onSubmit: (form: DefaultTaskFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
};

const DefaultTaskForm: FC<DefaultTaskFormProps> = ({ task, onSubmit, isDisabled, isLoading }: DefaultTaskFormProps) => {
  const i18n = useI18n();
  const taskCategoriesApi = useDefaultTaskCategoriesApi();
  const typesApi = useDueDateTypesApi();
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);

  const query: PaginationQueryWithFields<DueDateType> = {
    fields: DUE_DATE_TYPE_FIELDS,
  };

  const types = typesApi.useAdminDueDateTypes(query, aborter.current.signal);

  const categoriesSwr = taskCategoriesApi.useAdminDefaultTaskCategories(aborter.current.signal);

  useEffect(() => {
    categoriesSwr.error && dispatch(showErrorNotification(i18n, categoriesSwr.error));
  }, [categoriesSwr.error]);

  const defaultValues = {
    name: task?.name,
    defaultDueDateType: task?.defaultDueDateType,
    categories: task?.categories ?? [],
  };

  const disableAll = isDisabled || !categoriesSwr.data;

  return (
    <Form<DefaultTaskFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={disableAll}
      onSubmit={() => onSubmit}
      itemCreationInfo={task}
    >
      {({ register, formState: { errors }, reset, watch, setValue, control }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [task]);

        const categories = watch("categories") ?? [];

        return (
          <FormFieldsContainer>
            <FormField label={i18n.t.common.name} error={errors.name}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={disableAll || isLoading}
                  {...register("name", {
                    required: true,
                  })}
                />
              )}
            </FormField>
            <FormField label={i18n.t.common.categories} isOptional>
              <MultiCheckbox
                values={
                  (categoriesSwr.data ?? DefaultTaskCategories)?.map((x) => ({
                    id: x.id,
                    title: x.id,
                    selected: categories.includes(x.id),
                  })) ?? []
                }
                disabled={disableAll || isLoading}
                onUpdate={(id, checked) => {
                  setValue("categories", checked ? [...categories, id] : categories.filter((x) => x !== id));
                }}
              />
            </FormField>
            <Controller
              name="defaultDueDateType"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <FormField label={i18n.t.common.type} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Select
                        menuPosition="fixed"
                        inputId={labelId}
                        options={types.data?.items}
                        isLoading={isLoading}
                        isDisabled={isDisabled || isLoading}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        isClearable
                        styles={reactSelectStyles<DueDateType>(isInvalid)}
                        {...field}
                      />
                    )}
                  </FormField>
                );
              }}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default DefaultTaskForm;
